import React, { FC, FormHTMLAttributes, useCallback, useMemo } from 'react';
import { useDispatchRequest, useQuery } from '@redux-requests/react';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';

import { urlRegex, t } from '../../../utils';
import DistributionForm from './DistributionForm';
import { AppsConstants } from '../store/constants';
import { IAppCountry, ICountry } from '../store/actions';
import { saveDistributionInfo } from '../store/actions/';
import { IAppLanguageData } from '../store/actions/saveDistributionInfo';

export interface IAppDistribution extends FormHTMLAttributes<HTMLFormElement> {
  id: string;
}

const AppDistribution: FC<IAppDistribution> = (props) => {
  const { id } = props;
  const dispatchRequest = useDispatchRequest();
  const {
    data: {
      categories,
      plan,
      copyright,
      languages,
      status,
      gallery,
      privacyUrl,
      tutorialUrl,
      allowedCountries,
    },
  } = useQuery({ type: AppsConstants.APP, requestKey: id });
  const { data: allCountries } = useQuery({ type: AppsConstants.APP_COUNTRIES });
  const transformedAllowedCountries = useMemo<string[]>(
    () => allowedCountries.map((country: IAppCountry) => country.country),
    [allowedCountries]
  );
  // transformation function
  const formTransform = useCallback(
    (values) => {
      const { allowedCountries: updatedAllowedCountries, languages } = values ?? {};
      const transformedAllowedCountries = updatedAllowedCountries.map((country: string) => {
        // transform countries to match saved before allowed country in case any billing data is saved inside the country
        const allowedCountryIndex = allowedCountries.findIndex(
          (allowedCountry: IAppCountry) => allowedCountry.country === country
        );
        if (allowedCountryIndex > -1) {
          const country = { ...allowedCountries[allowedCountryIndex] };
          delete country.currencyCode; // currencyCode is not on AppCountryPayload schema
          delete country.hasPayment; // hasPayment is not on AppCountryPayload schema
          return country;
        }
        return { country };
      });
      // 1. determine languages based on countries
      const newCountries = updatedAllowedCountries.map((updatedAllowedCountry: string) =>
        allCountries.find(
          (allowedCountry: ICountry) => allowedCountry.code === updatedAllowedCountry
        )
      );
      const newLanguageCodes = newCountries.reduce(
        (languageCodes: string[], country: ICountry) => [...languageCodes, ...country.languages],
        []
      );
      // 2. construct unique array of languages
      const uniqueNewLanguageCodes = newLanguageCodes.filter(
        (languageCode: string, index: number, self: string[]) => {
          return index === self.indexOf(languageCode);
        }
      );
      // 3. see in unique language codes array if the languages that have defined before and replace code with language object
      const formattedLanguages = uniqueNewLanguageCodes.map((languageCode: string) => {
        // if language exists already
        const existingLanguage = languages.find(
          (language: IAppLanguageData) => language.language === languageCode
        );
        if (existingLanguage) return existingLanguage;
        // send empty language
        return { language: languageCode };
      });
      // build payload
      return {
        id,
        ...values,
        languages: formattedLanguages,
        allowedCountries: transformedAllowedCountries,
      };
    },
    [id, allowedCountries, allCountries]
  );
  // validation function
  const formValidate = useCallback((values: { [key: string]: any }): any => {
    const { copyright, categories, privacyUrl, tutorialUrl, allowedCountries } = values;
    const errors: any = {};
    if (!allowedCountries?.length) errors.allowedCountries = t('errors.atLeastOneCountry.required');
    if (!categories?.length) errors.categories = t('errors.atLeastOneCategory.required');
    if (copyright && !copyright?.match(urlRegex)) errors.copyright = t('errors.url.invalid');
    if (privacyUrl && !privacyUrl?.match(urlRegex)) errors.privacyUrl = t('errors.url.invalid');
    if (tutorialUrl && !tutorialUrl?.match(urlRegex)) errors.tutorialUrl = t('errors.url.invalid');
    // return empty object if no errors
    const hasErrors =
      Object.keys(errors).length > 0 ||
      errors?.allowedCountries?.reduce(
        (hasError: boolean, countryErrObject: { [key: string]: string }) => {
          return hasError || Object.keys(countryErrObject).length > 0;
        },
        false
      );
    return hasErrors ? errors : {};
  }, []);
  // form submit function
  const handleFormSubmit = useCallback(
    async (values) => {
      // validate form
      const errors = formValidate(values);
      if (Object.keys(errors).length > 0) return errors;
      // transform values and create request payload
      const payload = formTransform(values);
      const { error } = await dispatchRequest(saveDistributionInfo(payload));
      if (error?.data?.errors?.length) {
        return { [FORM_ERROR]: 'errors.somethingWentWrong' };
      }
      return error;
    },
    [dispatchRequest, formValidate, formTransform]
  );

  return (
    <Form
      onSubmit={handleFormSubmit}
      initialValues={{
        categories,
        plan,
        copyright,
        languages,
        status,
        gallery,
        privacyUrl,
        tutorialUrl,
        allowedCountries: transformedAllowedCountries,
      }}
      render={DistributionForm}
    />
  );
};

export default AppDistribution;
